// docs/116 CI9: the hearthside storyteller's tales. Every TALE_MS of server time the old storyteller by
// the fire tells one, TALE_LEAD_MS into the slot (unless the bard is playing then: they wait for the next
// slot), one line every LINE_MS over their head: an opening ("Gather round…"), the deed in three or so
// lines, and a closing ("…and that's why we raise a cup to them."). The deed is the World's own: the
// chronicle's line for it (already in a storyteller's voice), a named foe defeated, a quest settled. A
// World with nothing done yet hears an old tale of the realm. Pure and seeded by the slot, so every
// client in the room tells the same tale at the same moment.
import { hashN } from '../layout';
import type { Deed } from './types';

export const TALE_MS = 150_000;
export const TALE_LEAD_MS = 20_000;
export const LINE_MS = 5_600;
/** The longest line a bubble carries before it is split. */
export const LINE_MAX = 70;

export interface Tale { lines: string[]; about: string; old: boolean }

const OPEN = ['Gather round, gather round…', 'Now hush, and I\'ll tell it true.', 'Did I ever tell you about this one?', 'Pull up a stool. This one\'s a good one.', 'Fill your cups, and listen…', 'I was there. Well. Nearly there.'];
const CLOSE = ['…and that\'s why we raise a cup to them.', '…and that, friends, is a true story. Mostly.', '…so mind your manners when they come in.', '…and the road\'s been safer since. Cheers to that!', '…and if you don\'t believe me, ask the cat.'];
const FOE = [
  (d: Deed) => [`There was a terror called ${d.name ?? 'the beast'}…`, `…folk barred their doors from ${d.place ?? 'here'} to the hills.`, `Then came ${names(d) ?? 'a band of heroes'}, steel out and stubborn.`, `${d.name ?? 'It'} fell${d.place ? ` at ${d.place}` : ''}. I've seen the scars myself.`],
  (d: Deed) => [`You've heard the name ${d.name ?? 'the brute'}, I'll wager.`, `Nobody dared go near ${d.place ?? 'that place'}.`, `Nobody but ${names(d) ?? 'that lot by the fire'}.`, `And ${d.name ?? 'it'} won't trouble anyone again.`],
];
const QUEST = [
  (d: Deed) => [`They called it ${d.name ? `"${d.name}"` : 'a hopeless errand'}, and shook their heads.`, `${names(d) ?? 'Our heroes'} went anyway${d.place ? `, out to ${d.place}` : ''}.`, 'Mud to the knees and trouble to the eyebrows…', 'And they came back with it done. Every word.'],
  (d: Deed) => [`${d.name ?? 'That business'}, now. You'll remember the fuss.`, `${names(d) ?? 'A brave band'} took it on when nobody else would.`, 'Not a soul thought they\'d see them again.', 'Well. Here we all are, aren\'t we?'],
];
/** CI9: tales of the realm, told when the World's own players have done nothing yet. */
export const OLD_TALES: ReadonlyArray<{ about: string; lines: readonly string[] }> = [
  { about: 'the Lantern Knight', lines: ['Long ago a knight walked the Lantern Road at night…', 'She carried no sword. Only a lamp, and a song.', 'Every wolf that heard it lay down and slept.', 'They say on still nights you can hear it yet.'] },
  { about: 'the goose of Harrow Mill', lines: ['There was once a goose at Harrow Mill…', 'Meanest bird in the realm. Bit a bishop.', 'When raiders came, it chased off the lot of them.', 'They gave it a medal. It ate the medal.'] },
  { about: 'the Drowned Bell', lines: ['Under the river there\'s a bell, drowned an age ago.', 'When the realm is in danger, it rings.', 'My granny heard it once, the year of the long frost.', 'And heroes came. They always come.'] },
  { about: 'the three brothers', lines: ['Three brothers set out to find the end of the world.', 'The first found gold, and stopped.', 'The second found a pretty face, and stopped.', 'The third found this inn. Smartest of the lot.'] },
  { about: 'the giant who wanted tea', lines: ['A giant once came down from the Grey Fells…', 'Everyone ran, but the innkeeper\'s gran.', 'She poured him a cup of tea. A very big cup.', 'He comes every midwinter now, and helps with the roof.'] },
];

function names(d: Deed): string | undefined {
  const hs = d.heroes?.filter(Boolean) ?? [];
  if (!hs.length) return undefined;
  return hs.length === 1 ? hs[0] : `${hs.slice(0, -1).join(', ')} and ${hs.at(-1)}`;
}

/** Split a long line at its sentences and clauses so each bubble holds at most `max` characters. */
export function splitLine(text: string, max = LINE_MAX): string[] {
  const t = text.trim().replace(/\s+/g, ' ');
  if (t.length <= max) return t ? [t] : [];
  const parts = t.match(/[^.!?;:]+[.!?;:]*\s*/g) ?? [t];
  const out: string[] = [];
  let cur = '';
  for (const p of parts) {
    if ((cur + p).trim().length <= max) { cur += p; continue; }
    if (cur.trim()) out.push(cur.trim());
    cur = p;
    // a single clause still too long: break at a comma or a space
    while (cur.trim().length > max) {
      const s = cur.trim();
      let at = s.lastIndexOf(', ', max);
      if (at < max / 2) at = s.lastIndexOf(' ', max);
      if (at <= 0) at = max;
      out.push(`${s.slice(0, at + (s[at] === ',' ? 1 : 0)).trim()}…`);
      cur = `…${s.slice(at + 1).trim()}`;
    }
  }
  if (cur.trim()) out.push(cur.trim());
  return out;
}

const pick = <T,>(xs: readonly T[], ...k: number[]): T => xs[Math.floor(hashN(...k) * xs.length)]!;

/** The tale told in `slot` (4–6 lines). The newer deeds are told more often. */
export function taleFor(deeds: readonly Deed[], slot: number, seed: number): Tale {
  const open = pick(OPEN, seed, slot, 1), close = pick(CLOSE, seed, slot, 2);
  if (!deeds.length) {
    const t = pick(OLD_TALES, seed, slot, 3);
    return { lines: [open, ...t.lines, close].slice(0, 6), about: t.about, old: true };
  }
  // weigh the newest deeds most: deed i of n (newest first) has weight n - i
  const sorted = [...deeds].sort((a, b) => b.atMs - a.atMs);
  const n = sorted.length, total = (n * (n + 1)) / 2;
  let r = Math.floor(hashN(seed, slot, 4) * total);
  let d = sorted[0]!;
  for (let i = 0; i < n; i++) { r -= n - i; if (r < 0) { d = sorted[i]!; break; } }
  let middle: string[];
  if (d.kind === 'chronicle') middle = splitLine(d.text).slice(0, 4);
  else middle = (d.kind === 'foe' ? pick(FOE, seed, slot, 5) : pick(QUEST, seed, slot, 6))(d).flatMap((l) => splitLine(l));
  if (d.kind === 'chronicle' && middle.length < 3 && d.heroes?.length) middle.push(`${names(d)}. Remember the name${d.heroes.length > 1 ? 's' : ''}.`);
  const lines = [open, ...middle.slice(0, 4), close];
  return { lines, about: d.name ?? d.text.slice(0, 40), old: false };
}

export interface TaleNow {
  tale: Tale;
  slot: number;
  /** The line being told now (−1 before the first). */
  line: number;
  startMs: number;
  /** When the last line is done (the room claps). */
  endMs: number;
}

/**
 * The tale being told at `nowMs`, if any: slot `floor(now / TALE_MS)`, from TALE_LEAD_MS in, one line a
 * LINE_MS. `busy(ms)` says the room is taken then (the bard playing): no tale that slot.
 */
export function taleAt(nowMs: number, deeds: readonly Deed[], seed: number, busy?: (ms: number) => boolean): TaleNow | undefined {
  const slot = Math.floor(nowMs / TALE_MS);
  const startMs = slot * TALE_MS + TALE_LEAD_MS;
  if (nowMs < startMs) return undefined;
  if (busy && (busy(startMs) || busy(startMs + 10_000))) return undefined;
  const tale = taleFor(deeds, slot, seed);
  const endMs = startMs + tale.lines.length * LINE_MS;
  if (nowMs >= endMs + 3_000) return undefined;
  return { tale, slot, line: Math.min(tale.lines.length - 1, Math.floor((nowMs - startMs) / LINE_MS)), startMs, endMs };
}
